/**
 * Logger utility for Frontend application
 * Logs to daily files and console (in development)
 * Never logs sensitive information (tokens, passwords, etc.)
 */

interface LogEntry {
  timestamp: string
  level: 'info' | 'warn' | 'error' | 'debug'
  message: string
  context?: Record<string, unknown>
  error?: {
    name: string
    message: string
    stack?: string
  }
}

class Logger {
  private currentDate: string
  private isBrowser: boolean
  private maxLogSize: number = 10 * 1024 * 1024 // 10MB max per file

  constructor() {
    this.currentDate = this.getCurrentDate()
    this.isBrowser = typeof window !== 'undefined'
    
    // Initialize logging
    this.initializeLogging()
  }

  private getCurrentDate(): string {
    const now = new Date()
    return now.toISOString().split('T')[0] // YYYY-MM-DD
  }

  private async initializeLogging(): Promise<void> {
    if (!this.isBrowser) {
      return // Server-side rendering, skip file logging
    }

    // Check if date changed and rotate log file
    const today = this.getCurrentDate()
    if (today !== this.currentDate) {
      this.currentDate = today
    }

    // Note: Browser security prevents direct file system access
    // We use localStorage with daily rotation based on date
  }

  private sanitizeContext(context?: Record<string, unknown>): Record<string, unknown> | undefined {
    if (!context) return undefined

    const sensitiveKeys = [
      'token',
      'password',
      'secret',
      'authorization',
      'auth',
      'apiKey',
      'api_key',
      'accessToken',
      'refreshToken',
      'jwt',
      'session',
      'cookie',
    ]

    const sanitized: Record<string, unknown> = {}
    
    for (const [key, value] of Object.entries(context)) {
      const lowerKey = key.toLowerCase()
      const isSensitive = sensitiveKeys.some(sk => lowerKey.includes(sk))
      
      if (isSensitive) {
        sanitized[key] = '[REDACTED]'
      } else if (typeof value === 'object' && value !== null) {
        sanitized[key] = this.sanitizeContext(value as Record<string, unknown>)
      } else {
        sanitized[key] = value
      }
    }

    return sanitized
  }

  private sanitizeError(error: Error): { name: string; message: string; stack?: string } {
    return {
      name: error.name,
      message: error.message,
      stack: import.meta.env.DEV ? error.stack : undefined, // Only include stack in dev
    }
  }

  private formatLogEntry(entry: LogEntry): string {
    const parts = [
      `[${entry.timestamp}]`,
      `[${entry.level.toUpperCase()}]`,
      entry.message,
    ]

    if (entry.context) {
      parts.push(`Context: ${JSON.stringify(entry.context)}`)
    }

    if (entry.error) {
      parts.push(`Error: ${entry.error.name} - ${entry.error.message}`)
      if (entry.error.stack) {
        parts.push(`Stack: ${entry.error.stack}`)
      }
    }

    return parts.join(' ')
  }

  private async writeToFile(entry: LogEntry): Promise<void> {
    if (!this.isBrowser) return

    try {
      // Store logs in localStorage (with size limit)
      const storageKey = `app_logs_${this.currentDate}`
      const existingLogs = localStorage.getItem(storageKey) || '[]'
      const logs: string[] = JSON.parse(existingLogs)
      
      const logLine = this.formatLogEntry(entry)
      logs.push(logLine)

      // Keep only last 1000 entries per day to prevent storage overflow
      if (logs.length > 1000) {
        logs.shift()
      }

      // Store back to localStorage
      const logsString = JSON.stringify(logs)
      if (logsString.length > this.maxLogSize) {
        // Remove oldest entries if too large
        const trimmed = logs.slice(-500)
        localStorage.setItem(storageKey, JSON.stringify(trimmed))
      } else {
        localStorage.setItem(storageKey, logsString)
      }

      // In development, also log to console
      if (import.meta.env.DEV) {
        const consoleMethod = entry.level === 'error' ? 'error' : 
                             entry.level === 'warn' ? 'warn' : 
                             entry.level === 'debug' ? 'debug' : 'log'
        console[consoleMethod](this.formatLogEntry(entry))
      }
    } catch (error) {
      // Fallback: just use console if localStorage fails
      console.error('Failed to write log to storage:', error)
      console.log(this.formatLogEntry(entry))
    }
  }

  private createLogEntry(
    level: LogEntry['level'],
    message: string,
    context?: Record<string, unknown>,
    error?: Error
  ): LogEntry {
    return {
      timestamp: new Date().toISOString(),
      level,
      message,
      context: this.sanitizeContext(context),
      error: error ? this.sanitizeError(error) : undefined,
    }
  }

  async info(message: string, context?: Record<string, unknown>): Promise<void> {
    const entry = this.createLogEntry('info', message, context)
    await this.writeToFile(entry)
  }

  async warn(message: string, context?: Record<string, unknown>): Promise<void> {
    const entry = this.createLogEntry('warn', message, context)
    await this.writeToFile(entry)
  }

  async error(message: string, error?: Error, context?: Record<string, unknown>): Promise<void> {
    const entry = this.createLogEntry('error', message, context, error)
    await this.writeToFile(entry)
  }

  async debug(message: string, context?: Record<string, unknown>): Promise<void> {
    if (import.meta.env.DEV) {
      const entry = this.createLogEntry('debug', message, context)
      await this.writeToFile(entry)
    }
  }

  /**
   * Export logs for a specific date as downloadable file
   */
  async exportLogs(date?: string): Promise<void> {
    const targetDate = date || this.currentDate
    const storageKey = `app_logs_${targetDate}`
    const logs = localStorage.getItem(storageKey)

    if (!logs) {
      console.warn(`No logs found for date: ${targetDate}`)
      return
    }

    try {
      const logsArray: string[] = JSON.parse(logs)
      const content = logsArray.join('\n')
      const blob = new Blob([content], { type: 'text/plain' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `fixdauto-frontend-${targetDate}.log`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Failed to export logs:', error)
    }
  }

  /**
   * Clear old logs (older than specified days)
   */
  clearOldLogs(daysToKeep: number = 7): void {
    const cutoffDate = new Date()
    cutoffDate.setDate(cutoffDate.getDate() - daysToKeep)

    // Clear logs older than cutoff date
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key?.startsWith('app_logs_')) {
        const dateStr = key.replace('app_logs_', '')
        const logDate = new Date(dateStr)
        if (logDate < cutoffDate) {
          localStorage.removeItem(key)
        }
      }
    }
  }
}

// Export singleton instance
export const logger = new Logger()

// Auto-cleanup old logs on initialization
if (typeof window !== 'undefined') {
  logger.clearOldLogs(7) // Keep 7 days of logs
}
